import React, { useCallback, useEffect, useRef, useState } from "react";
import DatePicker from "components/Common/DatePicker/datepicker";

const Search_By_Date = [
  { label: "Registration Date", value: "REGISTRATION_DATE" },
  { label: "Collection Date", value: "COLLECTION_DATE" },
  { label: "Receiving Date", value: "RECEIVING_DATE" },
  { label: "Approval Date", value: "APPROVAL_DATE" },
];

const REPORT_TYPES = [
  { label: "Department", value: "DEPARTMENT" },
  { label: "Investigation", value: "INVESTIGATION" },
  { label: "All", value: "ALL" },
];

const today = new Date().toISOString().split("T")[0];

const getDefaultFilters = () => ({
  lab_department_ids: [],
  investigation_ids: [],
  search_by_date: "REGISTRATION_DATE",
  date_from: today,
  date_to: today,
  group_by_report: "DEPARTMENT",
});

const cleanFilters = (filtersObj) => {
  const cleaned = {};
  Object.entries(filtersObj).forEach(([key, value]) => {
    if (key === "date_from" || key === "date_to" || key === "search_by_date") {
      cleaned[key] = value;
      return;
    }
    if (key === "group_by_report") {
      if (value && value !== "ALL") cleaned[key] = value;
      return;
    }
    if (Array.isArray(value)) {
      if (value.length > 0) cleaned[key] = value;
      return;
    }
    if (value !== "" && value !== null && value !== undefined) {
      cleaned[key] = value;
    }
  });
  return cleaned;
};

const CheckboxGrid = ({ label, options, selectedIds, onChange }) => {
  const allIds = options.map((o) => String(o.value));
  const selectedStrIds = (selectedIds || []).map(String);
  const allChecked =
    allIds.length > 0 && allIds.every((id) => selectedStrIds.includes(id));
  const someChecked = allIds.some((id) => selectedStrIds.includes(id));

  const handleSelectAll = (e) => {
    onChange(e.target.checked ? options.map((o) => o.value) : []);
  };

  const handleItemChange = (id, checked) => {
    if (checked) {
      onChange([...selectedIds, id]);
    } else {
      onChange(selectedIds.filter((v) => String(v) !== String(id)));
    }
  };

  return (
    <div className="filter-section-row">
      <div className="filter-section-label">
        <input
          className="form-check-input"
          type="checkbox"
          id={`${label}-select-all`}
          checked={allChecked}
          ref={(el) => {
            if (el) el.indeterminate = someChecked && !allChecked;
          }}
          onChange={handleSelectAll}
        />
        <label htmlFor={`${label}-select-all`} className="section-label-text">
          {label} :
        </label>
      </div>

      <div className="filter-section-items">
        {options.length === 0 ? (
          <span className="text-muted small">No options available</span>
        ) : (
          options.map((opt) => (
            <div className="filter-checkbox-item" key={opt.value}>
              <input
                className="form-check-input"
                type="checkbox"
                id={`${label}-${opt.value}`}
                checked={selectedStrIds.includes(String(opt.value))}
                onChange={(e) => handleItemChange(opt.value, e.target.checked)}
              />
              <label
                className="form-check-label"
                htmlFor={`${label}-${opt.value}`}
              >
                {opt.label}
              </label>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const Filter = ({
  filters,
  updateMultipleFilters,
  investigation_list,
  lab_department_list,
  onDepartmentChange,
}) => {
  const [localFilters, setLocalFilters] = useState(() => ({
    ...getDefaultFilters(),
    ...filters,
  }));

  const isSearching = useRef(false);

  useEffect(() => {
    if (onDepartmentChange) {
      onDepartmentChange(localFilters.lab_department_ids);
    }
    setLocalFilters((prev) => ({ ...prev, investigation_ids: [] }));
  }, [localFilters.lab_department_ids.join(",")]);

  const handleLocalChange = useCallback((name, value) => {
    setLocalFilters((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleDepartmentChange = useCallback((vals) => {
    setLocalFilters((prev) => ({
      ...prev,
      lab_department_ids: vals,
      investigation_ids: [],
    }));
  }, []);

  const handleSearch = useCallback(() => {
    if (isSearching.current) return;
    isSearching.current = true;
    const cleaned = cleanFilters(localFilters);
    updateMultipleFilters(cleaned);
    setTimeout(() => {
      isSearching.current = false;
    }, 500);
  }, [localFilters, updateMultipleFilters]);

  const handleReset = useCallback(() => {
    const defaultFilters = getDefaultFilters();
    setLocalFilters(defaultFilters);
    updateMultipleFilters({
      date_from: defaultFilters.date_from,
      date_to: defaultFilters.date_to,
    });
  }, [updateMultipleFilters]);

  const departmentOptions = (lab_department_list?.data || []).map((item) => ({
    value: item.id,
    label: item.department,
  }));

  const investigationOptions = (investigation_list?.data || []).map((item) => ({
    value: item.id,
    label: item.investigation_name,
  }));

  const reportType = localFilters.group_by_report;
  const showDepartment = reportType === "DEPARTMENT";
  const showInvestigation = reportType === "INVESTIGATION";

  const showInvestigationGrid =
    showInvestigation && localFilters.lab_department_ids.length > 0;

  return (
    <>
      <style>{`
        .filter-top-row {
          display: flex;
          align-items: flex-end;
          flex-wrap: wrap;
          gap: 16px;
          padding: 12px 16px;
          border-bottom: 1px solid #d0e4f0;
        }
        .filter-top-row .filter-field {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .filter-top-row .filter-field label {
          font-size: 13px;
          font-weight: 600;
          color: #333;
          margin-bottom: 0;
        }
        .filter-top-row .filter-field select {
          font-size: 13px;
          min-width: 170px;
        }
        .filter-btn-group {
          display: flex;
          gap: 8px;
        }

        .filter-report-type-row {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 20px;
          padding: 10px 16px;
          border-bottom: 1px solid #d0e4f0;
          background: #f5faff;
        }
        .report-type-heading {
          font-size: 13px;
          font-weight: 700;
          color: #333;
          white-space: nowrap;
        }
        .filter-report-type-row .form-check {
          display: flex;
          align-items: center;
          gap: 5px;
          margin: 0;
        }
        .filter-report-type-row .form-check-label {
          font-size: 13px;
          color: #333;
          cursor: pointer;
          margin: 0;
        }

        .filter-section-row {
          display: flex;
          align-items: flex-start;
          padding: 10px 16px;
          border-bottom: 1px solid #d0e4f0;
          gap: 0;
        }
        .filter-section-label {
          display: flex;
          align-items: center;
          gap: 6px;
          min-width: 120px;
          padding-top: 3px;
          flex-shrink: 0;
        }
        .section-label-text {
          font-size: 13px;
          font-weight: 700;
          color: #333;
          cursor: pointer;
          white-space: nowrap;
          margin: 0;
        }
        .filter-section-items {
          display: flex;
          flex-wrap: wrap;
          gap: 4px 0px;
          flex: 1;
        }
        .filter-checkbox-item {
          display: flex;
          align-items: center;
          gap: 5px;
          width: 20%;
          min-width: 150px;
          padding: 2px 4px;
        }
        .filter-checkbox-item .form-check-label {
          font-size: 13px;
          color: #1a5fa8;
          cursor: pointer;
          margin: 0;
          line-height: 1.3;
        }
        .filter-checkbox-item .form-check-label:hover {
          text-decoration: underline;
        }
        .filter-section-label .form-check-input,
        .filter-checkbox-item .form-check-input {
          cursor: pointer;
          flex-shrink: 0;
          margin-top: 0;
        }

        .investigation-hint {
          padding: 10px 16px;
          font-size: 13px;
          color: #888;
          font-style: italic;
          border-bottom: 1px solid #d0e4f0;
        }
      `}</style>

      <div className="boxBody w-100">
        <div className="heading-form">Search Options</div>

        <div className="filter-top-row">
          <div className="filter-field">
            <label>Search By Date</label>
            <select
              className="form-select form-select-sm"
              value={localFilters.search_by_date}
              onChange={(e) =>
                handleLocalChange("search_by_date", e.target.value)
              }
            >
              {Search_By_Date.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-field">
            <label>From Date</label>
            <DatePicker
              name="date_from"
              value={localFilters.date_from}
              onChange={(date) => handleLocalChange("date_from", date)}
            />
          </div>

          <div className="filter-field">
            <label>To Date</label>
            <DatePicker
              name="date_to"
              value={localFilters.date_to}
              onChange={(date) => handleLocalChange("date_to", date)}
            />
          </div>

          <div className="filter-btn-group">
            <button className="btn btn-primary btn-sm" onClick={handleSearch}>
              Search
            </button>
            <button className="btn btn-secondary btn-sm" onClick={handleReset}>
              Reset
            </button>
          </div>
        </div>

        <div className="filter-report-type-row">
          <span className="report-type-heading">Type of Report :</span>
          {REPORT_TYPES.map((rt) => (
            <div className="form-check" key={rt.value}>
              <input
                className="form-check-input"
                type="radio"
                name="group_by_report"
                id={`report-type-${rt.value}`}
                value={rt.value}
                checked={localFilters.group_by_report === rt.value}
                onChange={() => handleLocalChange("group_by_report", rt.value)}
              />
              <label
                className="form-check-label"
                htmlFor={`report-type-${rt.value}`}
              >
                {rt.label}
              </label>
            </div>
          ))}
        </div>

        {(showDepartment || showInvestigation) && (
          <CheckboxGrid
            label="Departments"
            options={departmentOptions}
            selectedIds={localFilters.lab_department_ids}
            onChange={handleDepartmentChange}
          />
        )}

        {showInvestigation &&
          (showInvestigationGrid ? (
            <CheckboxGrid
              label="Test"
              options={investigationOptions}
              selectedIds={localFilters.investigation_ids}
              onChange={(vals) => handleLocalChange("investigation_ids", vals)}
            />
          ) : (
            <div className="investigation-hint">
              ℹ️ Please select a department to view investigations.
            </div>
          ))}

        {reportType === "ALL" && (
          <div className="investigation-hint">
            ℹ️ All departments and investigations will be included.
          </div>
        )}
      </div>
    </>
  );
};

export default Filter;
